/**
 * Bound a cancellable upload without racing recovery against live network
 * work. The timeout aborts first, then this helper still awaits the upload's
 * terminal cancellation rejection before returning control to reconciliation.
 */
export async function uploadFeedbackScreenshotWithTimeout<T>(
  start: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await start(controller.signal);
  } catch (error) {
    if (timedOut) {
      throw new Error('The screenshot upload timed out and was safely canceled.');
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Upload a report's screenshots as one set (up to 5, DECISIONS 123 item 12),
 * each file to its OWN path, in order, one at a time so each has the whole
 * connection and its own timeout. The first failure stops the rest and
 * rejects, so the caller never finalizes a set that is missing a file: the
 * set attaches together or not at all.
 *
 * The upload itself is a parameter so this order, and the stop, are tested
 * without Firebase (tests/feedback-upload-lifecycle.test.mjs).
 */
export async function uploadFeedbackScreenshotSet<F>(
  files: readonly F[],
  paths: readonly string[],
  upload: (file: F, path: string) => Promise<unknown>,
  onSending?: (number: number, total: number) => void,
): Promise<void> {
  if (files.length !== paths.length) {
    throw new Error('Each screenshot needs its own storage path.');
  }
  for (let index = 0; index < files.length; index += 1) {
    try {
      onSending?.(index + 1, files.length);
    } catch {
      // A progress label can never be the reason a screenshot fails.
    }
    await upload(files[index], paths[index]);
  }
}
